import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bug, AlertCircle, CalendarCheck, FileText, ClipboardList, Award } from 'lucide-react'
import DashboardModal from './DashboardModal'
import type { ActionButton } from './DashboardModal'
import type { QhseDerivedKpis } from './types'

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const statusBadge = (status?: string | null) => {
  const s = (status || '').toUpperCase()
  if (s === 'CLOSE') return 'bg-emerald-50 text-emerald-600'
  if (s === 'SUBMIT') return 'bg-amber-50 text-amber-600'
  return 'bg-slate-50 text-slate-600'
}

const severityColor = (cat?: string | null) => {
  if (!cat) return 'bg-slate-100 text-slate-600'
  if (cat.includes('CRITICAL') || cat === 'THIRD_PARTY_INSPECTION') return 'bg-red-50 text-red-600'
  if (cat === 'SHIP_OBSERVATION') return 'bg-amber-50 text-amber-600'
  return 'bg-blue-50 text-blue-600'
}

/* ─── Helpers ─── */
function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

/* ─── KpiDetailModal ─── */
interface KpiDetailProps {
  open: boolean
  onClose: () => void
  kpis: QhseDerivedKpis
}

export const KpiDetailModal: FC<KpiDetailProps> = ({ open, onClose, kpis }) => {
  const items = [
    { label: 'Open Defects', value: kpis.openDefects, icon: Bug, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Near Misses (YTD)', value: kpis.nearMissesYTD, icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Open Audits', value: kpis.openAudits, icon: CalendarCheck, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Open Work Permits', value: kpis.openWorkPermits, icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Pending Inspections', value: kpis.pendingInspections, icon: ClipboardList, color: 'text-rose-600', bg: 'bg-rose-50' },
    { label: 'Expiring Certs', value: kpis.expiringCerts, icon: Award, color: 'text-teal-600', bg: 'bg-teal-50' },
  ]

  const actions: ActionButton[] = [
    {
      label: 'Download Summary',
      onClick: () => downloadCsv('qhse-kpi-summary.csv',
        ['Metric', 'Value'],
        items.map((i) => [i.label, String(i.value)]),
      ),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='KPI Details' actions={actions}>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
        {items.map((item, i) => (
          <div key={i} className={`flex flex-col items-center justify-center p-4 rounded-xl ${item.bg}`}>
            <item.icon className={`w-6 h-6 ${item.color} mb-2`} />
            <div className={`text-2xl font-bold ${item.color}`}>{item.value}</div>
            <div className='text-[10px] text-slate-500 mt-1 text-center'>{item.label}</div>
          </div>
        ))}
      </div>
    </DashboardModal>
  )
}

/* ─── DefectModal (Full Defects Table) ─── */
interface DefectModalProps {
  open: boolean
  onClose: () => void
  defects: any[]
}

export const DefectModal: FC<DefectModalProps> = ({ open, onClose, defects }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = defects.filter((d: any) =>
    (d.vesselName || '').toLowerCase().includes(q) ||
    (d.description || '').toLowerCase().includes(q) ||
    (d.category || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('defects.csv',
        ['Vessel', 'Description', 'Category', 'Date', 'Status'],
        filtered.map((d: any) => [d.vesselName || '', d.description || '', d.category || '', fmtDate(d.dateObserved), d.status || 'SAVE']),
      ),
    },
    {
      label: 'View Defects',
      variant: 'primary',
      onClick: () => navigate('/qhse/defects-list'),
    },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='All Defects & Observations' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel, description or category...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Description</th>
              <th className='text-left font-semibold py-2 px-2'>Category</th>
              <th className='text-left font-semibold py-2 px-2'>Date</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className='text-center text-slate-400 py-8 text-xs'>No defects found</td>
              </tr>
            ) : (
              filtered.map((d: any, i: number) => (
                <tr key={d.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{d.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700 max-w-[250px] truncate'>{d.description || '—'}</td>
                  <td className='py-2.5 px-2'>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${severityColor(d.category)}`}>
                      {d.category || '—'}
                    </span>
                  </td>
                  <td className='py-2.5 px-2 text-slate-500'>{fmtDate(d.dateObserved)}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${statusBadge(d.status)}`}>{d.status || 'SAVE'}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── NearMissModal (Full Near Misses Table) ─── */
interface NearMissModalProps {
  open: boolean
  onClose: () => void
  nearMisses: any[]
}

export const NearMissModal: FC<NearMissModalProps> = ({ open, onClose, nearMisses }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = nearMisses.filter((nm: any) =>
    (nm.vesselName || '').toLowerCase().includes(q) ||
    (nm.descriptionOfOccurrence || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('near-misses.csv',
        ['Date', 'Vessel', 'Description', 'Status'],
        filtered.map((nm: any) => [fmtDate(nm.dateOfOccurrence || nm.dateIssued), nm.vesselName || '', nm.descriptionOfOccurrence || nm.occurrenceTypes?.join(', ') || '', nm.status || 'SAVE']),
      ),
    },
    {
      label: 'View Near Misses',
      variant: 'primary',
      onClick: () => navigate('/qhse/near-miss-reports'),
    },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='All Near Miss Reports' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel or description...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Date</th>
              <th className='text-left font-semibold py-2 px-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Description</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No near miss reports found</td>
              </tr>
            ) : (
              filtered.map((nm: any, i: number) => (
                <tr key={nm.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 text-slate-700'>{fmtDate(nm.dateOfOccurrence || nm.dateIssued)}</td>
                  <td className='py-2.5 px-2 font-semibold text-slate-800'>{nm.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500 max-w-[250px] truncate'>
                    {nm.descriptionOfOccurrence || nm.occurrenceTypes?.join(', ') || '—'}
                  </td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${statusBadge(nm.status)}`}>{nm.status || 'SAVE'}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── AuditModal (Full Audit Schedule) ─── */
interface AuditModalProps {
  open: boolean
  onClose: () => void
  audits: any[]
}

export const AuditModal: FC<AuditModalProps> = ({ open, onClose, audits }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = audits.filter((a: any) =>
    (a.vesselName || '').toLowerCase().includes(q) ||
    (a.auditKindName || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('audits.csv',
        ['Vessel', 'Type', 'Date', 'Status'],
        filtered.map((a: any) => [a.vesselName || '', a.auditKindName || '', fmtDate(a.auditFromDate), a.isFinalized ? 'Finalized' : 'Open']),
      ),
    },
    {
      label: 'View Audits',
      variant: 'primary',
      onClick: () => navigate('/qhse/audit-index'),
    },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='All Audits' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel or type...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Type</th>
              <th className='text-left font-semibold py-2 px-2'>Date</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No audits found</td>
              </tr>
            ) : (
              filtered.map((a: any, i: number) => (
                <tr key={a.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{a.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{a.auditKindName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700'>{fmtDate(a.auditFromDate)}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      a.isFinalized ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                    }`}>{a.isFinalized ? 'Finalized' : 'Open'}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── InspectionModal (Full Inspection List) ─── */
interface InspectionModalProps {
  open: boolean
  onClose: () => void
  inspections: any[]
}

export const InspectionModal: FC<InspectionModalProps> = ({ open, onClose, inspections }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = inspections.filter((i: any) =>
    (i.vesselName || '').toLowerCase().includes(q) ||
    (i.inspectionKindName || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('inspections.csv',
        ['Vessel', 'Type', 'Date', 'Status'],
        filtered.map((i: any) => [i.vesselName || '', i.inspectionKindName || '', fmtDate(i.inspectionFromDate), i.isFinalized ? 'Finalized' : 'Open']),
      ),
    },
    {
      label: 'View Inspections',
      variant: 'primary',
      onClick: () => navigate('/qhse/inspindex'),
    },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='All Inspections' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel or type...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Type</th>
              <th className='text-left font-semibold py-2 px-2'>Date</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No inspections found</td>
              </tr>
            ) : (
              filtered.map((i: any, idx: number) => (
                <tr key={i.id ?? idx} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{i.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{i.inspectionKindName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700'>{fmtDate(i.inspectionFromDate)}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      i.isFinalized ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                    }`}>{i.isFinalized ? 'Finalized' : 'Open'}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── WorkPermitModal (Full Work Permits Table) ─── */
interface WorkPermitModalProps {
  open: boolean
  onClose: () => void
  workPermits: any[]
}

export const WorkPermitModal: FC<WorkPermitModalProps> = ({ open, onClose, workPermits }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = workPermits.filter((wp: any) =>
    (wp.permitName || wp.formNumber || '').toLowerCase().includes(q) ||
    (wp.vesselName || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('work-permits.csv',
        ['Permit', 'Vessel', 'Start', 'Status'],
        filtered.map((wp: any) => [wp.permitName || wp.formNumber || `#${wp.id}`, wp.vesselName || '', fmtDate(wp.startDate), wp.status || 'SAVE']),
      ),
    },
    {
      label: 'View Permits',
      variant: 'primary',
      onClick: () => navigate('/qhse/work-permits-and-checklists'),
    },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='Open Work Permits' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by permit or vessel...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Permit</th>
              <th className='text-left font-semibold py-2 px-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Start</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No work permits found</td>
              </tr>
            ) : (
              filtered.map((wp: any, i: number) => (
                <tr key={wp.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{wp.permitName || wp.formNumber || `#${wp.id}`}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{wp.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-700'>{fmtDate(wp.startDate)}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      wp.status === 'CLOSE' ? 'bg-emerald-50 text-emerald-600' :
                      wp.status === 'SUBMIT' ? 'bg-amber-50 text-amber-600' :
                      'bg-slate-50 text-slate-600'
                    }`}>{wp.status || 'SAVE'}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}
